import { Directive, ElementRef, Input, OnInit, Renderer2 } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { HelpCenterDialogComponent } from '../components/help-center-dialog/help-center-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { ExistHelpCenterResp } from 'src/app/features/setting/pages/help-center-setting/model/ExistHelpCenterResp';
import { HelpCenterResp } from 'src/app/features/setting/pages/help-center-setting/model/HelpCenterResp';
import { HelpCenterService } from 'src/app/features/setting/service/help-center.service';

@Directive({
  selector: '[helpCenterButton]',
})
export class HelpCenterButtonDirective implements OnInit {
  @Input('helpCenterButton') permissionCode: string | null | undefined = null;
  @Input() position: 'inline' | 'right' = 'inline';
  @Input() lang: string = 'zh-tw';

  private iconWrapper?: HTMLElement;
  private unlistenClick?: () => void;
  private unlistenKey?: () => void;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private dialogService: DialogService,
    private helpCenterService: HelpCenterService,
    private translateService: TranslateService
  ) {}

  async ngOnInit(): Promise<void> {
    console.log('btn loading: ' + this.permissionCode);
    if (!this.permissionCode) return;

    let existResp: ExistHelpCenterResp;
    try {
      existResp = await firstValueFrom(
        this.helpCenterService.existHelpCenter(this.permissionCode, this.lang)
      );
    } catch (error) {
      console.warn('existHelpCenter request failed', error);
      return;
    }

    // 沒任何可顯示內容就不插 icon
    if (!existResp?.helpCenterExist && !existResp?.helpCenterITExist && !existResp?.userFileExist)
      return;

    // 避免重複掛
    if (this.iconWrapper) return;

    const iconWrapper = this.renderer.createElement('span');
    const icon = this.renderer.createElement('i');
    this.renderer.addClass(icon, 'pi');
    this.renderer.addClass(icon, 'pi-question-circle');
    this.renderer.setStyle(icon, 'cursor', 'pointer');
    this.renderer.setStyle(icon, 'color', '#666');
    this.renderer.setStyle(icon, 'font-size', '1.4rem');
    this.renderer.setAttribute(icon, 'tabindex', '0'); // 可鍵盤聚焦

    const tip = await firstValueFrom(
      this.translateService.get('HelpCenter.Button.OpenGuide')
    );
    this.renderer.setAttribute(icon, 'title', tip);

    if (this.position === 'right') {
      this.renderer.setStyle(iconWrapper, 'position', 'absolute');
      this.renderer.setStyle(iconWrapper, 'top', '0.25rem');
      this.renderer.setStyle(iconWrapper, 'right', '0.5rem');
      this.renderer.setStyle(iconWrapper, 'z-index', '10');
      this.renderer.setStyle(this.el.nativeElement, 'position', 'relative');
    } else {
      this.renderer.setStyle(icon, 'margin-left', '0.5rem');
    }
    this.renderer.setStyle(iconWrapper, 'vertical-align', 'middle');

    this.unlistenClick = this.renderer.listen(icon, 'click', () => {
      void this.openGuideDialog();
    });

    this.unlistenKey = this.renderer.listen(
      icon,
      'keydown',
      (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          void this.openGuideDialog();
        }
      }
    );

    this.renderer.appendChild(iconWrapper, icon);
    this.renderer.appendChild(this.el.nativeElement, iconWrapper);
    this.iconWrapper = iconWrapper;
  }

  ngOnDestroy(): void {
    try {
      this.unlistenClick?.();
    } catch {}
    try {
      this.unlistenKey?.();
    } catch {}
    this.unlistenClick = this.unlistenKey = undefined;
    if (this.iconWrapper) {
      try {
        this.renderer.removeChild(this.el.nativeElement, this.iconWrapper);
      } catch {}
      this.iconWrapper = undefined;
    }
  }

  /**
   * 開啟說明 Dialog
   */
  private async openGuideDialog(): Promise<void> {

    if (!this.permissionCode) {
      console.error(' permissionCode 是空的');
      return;
    }

    // 取得標題
    let headerTitle = await firstValueFrom(
      this.translateService.get('HelpCenter.Action.View')
    );

    try {
      const guideResp: HelpCenterResp = await firstValueFrom(
        this.helpCenterService.getHelpCenterByPermissionCode(this.permissionCode)
      );
      headerTitle = `${headerTitle} - ${guideResp.guideHeader ?? this.permissionCode}`;
    } catch (error) {
      console.warn('取得標題失敗，使用預設標題', error);
      headerTitle = `${headerTitle} - ${this.permissionCode}`;
    }

  // 開啟 Dialog
  this.dialogService.open(HelpCenterDialogComponent, {
    data: {
      permissionCode: this.permissionCode,
      lang: this.lang ?? 'zh-tw',
    },
    header: headerTitle,
    width: '60vw',
    height: '80vh',
    dismissableMask: true,
    closable: true,
  });

    console.log(' Dialog 已開啟');
  }
}
